import { BaseGrid } from '@/grid/BaseGrid';
import { Cell } from '@/grid/Cell';
import { Cluster, ClusterBounds } from './Cluster';

export class ClusterGrid {
  private grid: BaseGrid;
  private clusterSize: number;
  private clusters: Cluster[][] = [];
  private cellToCluster: Map<Cell, Cluster> = new Map();
  private clustersFlat: Cluster[] = [];
  private nextClusterId: number = 0;

  constructor(grid: BaseGrid, clusterSize: number) {
    this.grid = grid;
    this.clusterSize = Math.max(clusterSize, 3);
    this.buildClusters();
  }

  private buildClusters(): void {
    const gridRows = this.grid.rows;
    const gridCols = this.grid.cols;

    const numClusterRows = Math.ceil(gridRows / this.clusterSize);
    const numClusterCols = Math.ceil(gridCols / this.clusterSize);

    this.clusters = [];

    for (let clusterRow = 0; clusterRow < numClusterRows; clusterRow++) {
      this.clusters[clusterRow] = [];

      for (let clusterCol = 0; clusterCol < numClusterCols; clusterCol++) {
        const bounds = this.calculateClusterBounds(
          clusterRow,
          clusterCol,
          gridRows,
          gridCols
        );

        const cells: Cell[] = [];
        for (let row = bounds.minRow; row <= bounds.maxRow; row++) {
          for (let col = bounds.minCol; col <= bounds.maxCol; col++) {
            const cell = this.grid.getCell(row, col);
            if (cell) {
              cells.push(cell);
            }
          }
        }

        const cluster = new Cluster(
          this.nextClusterId++,
          clusterRow,
          clusterCol,
          bounds,
          cells
        );

        this.clusters[clusterRow][clusterCol] = cluster;
        this.clustersFlat.push(cluster);

        for (const cell of cells) {
          this.cellToCluster.set(cell, cluster);
        }
      }
    }
  }

  private calculateClusterBounds(
    clusterRow: number,
    clusterCol: number,
    gridRows: number,
    gridCols: number
  ): ClusterBounds {
    const minRow = clusterRow * this.clusterSize;
    const maxRow = Math.min(minRow + this.clusterSize - 1, gridRows - 1);
    const minCol = clusterCol * this.clusterSize;
    const maxCol = Math.min(minCol + this.clusterSize - 1, gridCols - 1);

    return { minRow, maxRow, minCol, maxCol };
  }

  getCluster(clusterRow: number, clusterCol: number): Cluster | null {
    if (
      clusterRow >= 0 &&
      clusterRow < this.clusters.length &&
      clusterCol >= 0 &&
      clusterCol < this.clusters[clusterRow].length
    ) {
      return this.clusters[clusterRow][clusterCol];
    }
    return null;
  }

  getClusterForCell(cell: Cell): Cluster | null {
    return this.cellToCluster.get(cell) || null;
  }

  getClusterSize(): number {
    return this.clusterSize;
  }

  getAllClusters(): Cluster[] {
    return this.clustersFlat;
  }

  getNumClusterRows(): number {
    return this.clusters.length;
  }

  getNumClusterCols(): number {
    return this.clusters[0]?.length || 0;
  }

  getAdjacentClusters(cluster: Cluster): Cluster[] {
    const adjacent: Cluster[] = [];
    const { row, col } = cluster;

    for (let dr = -1; dr <= 1; dr++) {
      for (let dc = -1; dc <= 1; dc++) {
        if (dr === 0 && dc === 0) continue;

        const neighbor = this.getCluster(row + dr, col + dc);
        if (neighbor) {
          adjacent.push(neighbor);
        }
      }
    }

    return adjacent;
  }
}
